import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

import { useQueryBuilder, usersFilters, useUsers } from "~/hooks";
import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { MainLayout } from "~/router/layouts";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { Button, usePageTitle } from "~/shared";
import { getRoleLabel, ROLES, User } from "~/shared.types";
import { formatStoredPhoneNumber } from "~/shared/utils/format-stored-phone-number";
import { icons } from "~/ui";
import { PaginatedTable } from "~/ui/common/PaginatedTable";
import { formatDateField } from "~/utils";
import { DropdownOption, Title } from "../components";
import { SearchInput } from "../components/SearchInput";

const options: DropdownOption[] = [
  { id: "", label: "All" },
  ...Object.values(ROLES).map((role) => ({
    id: role.value,
    label: role.label,
  })),
  { id: "is-test", label: "Test Users" },
];

export const Dashboard = () => {
  usePageTitle("");
  const navigateModal = useNavigateModal();
  const columns: ColumnDef<User>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => {
        const name = row.original.firstName + " " + row.original.lastName;
        return (
          <span className="block max-w-xs truncate" title={name}>
            {name || "-"}
          </span>
        );
      },
    },
    {
      id: "email",
      header: "Email",
      accessorKey: "email",
    },
    {
      id: "ehrId",
      header: "Patient ID",
      accessorKey: "ehrId",
      cell: ({ row }) => {
        const ehrId = row.original.ehrId;
        return ehrId || "-";
      },
    },
    {
      id: "userType",
      header: "User Type",
      accessorKey: "userType",
      cell: ({ row }) => {
        const value = row.original.userType;

        return getRoleLabel(value);
      },
    },
    {
      id: "source",
      header: "Source",
      accessorKey: "source",
    },
    {
      id: "lastLoginDate",
      header: "Last Hub Log In Date (ET)",
      accessorKey: "lastLoginDate",
      cell: ({ row }) => formatDateField(row.original, "lastLoginDate", "-"),
    },
    {
      id: "phoneNumber",
      header: "Mobile phone number",
      accessorKey: "phoneNumber",
      enableSorting: false,
      cell: ({ row }) => {
        const phoneNumber = row.original.phoneNumber;
        return phoneNumber ? formatStoredPhoneNumber(phoneNumber) : "-";
      },
    },
  ];
  const [searchParams] = useSearchParams();

  const querySearch = searchParams.get("search") ?? "";
  const queryRole = searchParams.get("role") ?? "";

  const selectedRole = queryRole && options.find((op) => op.id === queryRole);

  const [selected, setSelected] = useState(selectedRole || options[0]);
  const [search, setSearch] = useState(decodeURIComponent(querySearch));
  const { canCreateUser } = usePermissions();

  const {
    builder: usersBuilder,
    setCurrentPage,
    setSorting,
    sortingState,
    setFilters,
  } = useQueryBuilder({
    builderFilters: [
      {
        attribute: "role",
        value: [queryRole],
      },
      {
        attribute: "search",
        value: [querySearch],
      },
    ],
    aliases: usersFilters,
  });

  const { getUsers } = useUsers();

  const { data, isLoading } = getUsers({
    queryBuilder: usersBuilder,
  });

  const handleRoleFilter = (option: DropdownOption) => {
    setSelected(option);

    setCurrentPage(1);

    setFilters([{ attribute: "role", value: option.id }]);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setCurrentPage(1);

    setFilters([
      {
        attribute: "search",
        value: [encodeURIComponent(search)],
      },
    ]);
  };

  const handleRegisterPatient = () => {
    navigateModal(MODAL_ROUTES.staffBooking);
  };

  const handleAddUser = () => {
    navigateModal(MODAL_ROUTES.addUser);
  };

  const handleRowClick = (row: User) => {
    navigateModal(`${MODAL_ROUTES.userDetails}/${row.id}`);
  };

  return (
    <MainLayout>
      <div className="flex flex-col justify-between pb-8 md:flex-row">
        <h1 className="text-indigo font-ivy text-[32px]">User Dashboard</h1>

        <div className="flex flex-col gap-4 md:flex-row">
          <Button
            variant="secondary"
            size="sm"
            className="gap-2 text-base"
            onClick={handleRegisterPatient}
          >
            <icons.outline.CalendarIcon className="size-6" />
            Register a new patient
          </Button>
          <SearchInput
            setSearch={setSearch}
            search={search}
            handleOnSubmit={handleSearch}
            placeholder="Search for admins or users..."
          />
        </div>
      </div>

      <Title
        title="Users"
        action={
          canCreateUser && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-base"
              onClick={handleAddUser}
            >
              <icons.outline.UserPlusIcon className="size-5" />
              Add user
            </Button>
          )
        }
        options={options}
        selected={selected}
        onChange={handleRoleFilter}
        className="pb-4"
        titleClassName="text-indigo"
      />

      <hr className="border-soft-pastel-lavender" />

      <div className="pt-3">
        <PaginatedTable
          data={data?.data}
          pagination={data?.pagination}
          columns={columns}
          setCurrentPage={setCurrentPage}
          sorting={sortingState}
          setSorting={setSorting}
          isLoadingData={isLoading}
          onRowClick={handleRowClick}
        />
      </div>
    </MainLayout>
  );
};
